import { mockCommitments } from "src/lib/utils/mock";
import type { IClientsOverviewResponse } from "src/services/api/clientsOverview/types";
import type { ObjectiveDraftResponse } from "src/services/api/opportunities/draftTypes";
import type { IOpportunitiesResponse } from "src/services/api/opportunities/types";
import type {
    IExecutionPerformanceResponse,
    ExecutionAdvisorPerformance,
    ExecutionEntityPerformance,
    ExecutionPerformancePoint,
} from "src/services/api/executionPerformance/types";
import type { QuestionResponse } from "src/types/chat";
import type { HistoryMessage } from "src/services/api/chat";

export const DESIGN_BACKEND_TOKEN = "design-mode-mock-token";

export const mockClientsOverview: IClientsOverviewResponse = {
    number_of_clients: 128,
    total_amount: 48_750_000,
    top_clients: [
        {
            cliente: { name: "Maria Silva", account: 5990706, perfil: "MODERADO" },
            net_worth: 1_250_000,
            position: 1,
        },
        {
            cliente: { name: "Lucas Pereira", account: 8001640, perfil: "SOFISTICADO" },
            net_worth: 980_500,
            position: 2,
        },
        {
            cliente: { name: "Henrique Costa", account: 14781264, perfil: "CONSERVADOR" },
            net_worth: 875_200,
            position: 3,
        },
    ],
};

export const mockOpportunities: IOpportunitiesResponse = {
    objectives: [
        {
            objective_id: "design-obj-1",
            title: "Caixa acima de 10% — renda fixa",
            source: "design-mode",
            text: "Priorizar clientes com caixa elevado e perfil moderado ou sofisticado.",
            createdDate: "2026-03-01",
            endDate: "2026-03-31",
            dueDate: "2026-03-31",
            isExpired: false,
            impacted_clients: [
                { account: "005990706", name: "Maria Silva", advisor_name: "Hugo Dowsley", volumeOportunidade: 125_000 },
                { account: "003221190", name: "Roberto Alves", advisor_name: "Hugo Dowsley", volumeOportunidade: 90_000 },
                { account: "008001640", name: "Lucas Pereira", advisor_name: "Ana Souza", volumeOportunidade: 88_500 },
                { account: "010773410", name: "Fernanda Lima", advisor_name: "Ana Souza", volumeOportunidade: 60_000 },
                { account: "014781264", name: "Henrique Costa", advisor_name: "Paulo Oliveira", volumeOportunidade: 210_000 },
                { account: "011902255", name: "Juliana Rocha", advisor_name: "Paulo Oliveira", volumeOportunidade: 70_000 },
                { account: "009440132", name: "Marcos Tavares", advisor_name: "Carla Mendes", volumeOportunidade: 150_000 },
            ],
            has_movements_cypher: true,
            movement_portfolio_rollups: [
                { portfolio_account: "005990706", account: "005990706", total_amount_net: 42_000, portfolio_name: "Maria Silva", movement_count: 3, investment_advisor: { name: "Hugo Dowsley" } },
                { portfolio_account: "003221190", account: "003221190", total_amount_net: 81_000, portfolio_name: "Roberto Alves", movement_count: 2, investment_advisor: { name: "Hugo Dowsley" } },
                { portfolio_account: "008001640", account: "008001640", total_amount_net: 35_400, portfolio_name: "Lucas Pereira", movement_count: 2, investment_advisor: { name: "Ana Souza" } },
                { portfolio_account: "010773410", account: "010773410", total_amount_net: 12_000, portfolio_name: "Fernanda Lima", movement_count: 1, investment_advisor: { name: "Ana Souza" } },
                { portfolio_account: "014781264", account: "014781264", total_amount_net: 168_000, portfolio_name: "Henrique Costa", movement_count: 4, investment_advisor: { name: "Paulo Oliveira" } },
                { portfolio_account: "011902255", account: "011902255", total_amount_net: 70_000, portfolio_name: "Juliana Rocha", movement_count: 2, investment_advisor: { name: "Paulo Oliveira" } },
                { portfolio_account: "009440132", account: "009440132", total_amount_net: 30_000, portfolio_name: "Marcos Tavares", movement_count: 1, investment_advisor: { name: "Carla Mendes" } },
            ],
            summary: {
                total_amount_net: 438_400,
                portfolio_count: 7,
                movement_count: 15,
            },
        },
        {
            objective_id: "design-obj-2",
            title: "Vencimentos LCI — próximos 15 dias",
            source: "design-mode",
            text: "Clientes com produtos a vencer em renda fixa pós-fixada.",
            createdDate: "2026-03-10",
            endDate: "2026-03-25",
            dueDate: "2026-03-25",
            isExpired: false,
            impacted_clients: [
                { account: "014781264", name: "Henrique Costa", advisor_name: "Paulo Oliveira", volumeOportunidade: 210_000 },
                { account: "007654321", name: "Beatriz Nunes", advisor_name: "Ana Souza", volumeOportunidade: 95_000 },
                { account: "002345678", name: "Rafael Dias", advisor_name: "Hugo Dowsley", volumeOportunidade: 130_000 },
            ],
            has_movements_cypher: true,
            movement_portfolio_rollups: [
                { portfolio_account: "014781264", account: "014781264", total_amount_net: 105_000, portfolio_name: "Henrique Costa", movement_count: 3, investment_advisor: { name: "Paulo Oliveira" } },
                { portfolio_account: "007654321", account: "007654321", total_amount_net: 19_000, portfolio_name: "Beatriz Nunes", movement_count: 1, investment_advisor: { name: "Ana Souza" } },
                { portfolio_account: "002345678", account: "002345678", total_amount_net: 117_000, portfolio_name: "Rafael Dias", movement_count: 2, investment_advisor: { name: "Hugo Dowsley" } },
            ],
            summary: {
                total_amount_net: 241_000,
                portfolio_count: 3,
                movement_count: 6,
            },
        },
    ],
};

export function buildMockDraftResponse(body?: {
    title?: string;
    text?: string;
    endDate?: string;
}): ObjectiveDraftResponse {
    const text = body?.text?.trim() || "Objetivo de exemplo em modo design.";
    return {
        draft_id: "design-draft-001",
        expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        objective: {
            title: body?.title?.trim() || "Rascunho design",
            text,
            endDate: body?.endDate ?? "2026-09-30",
            source: "design-mode",
        },
        interpretation:
            "Em modo design: a IA interpretaria este prompt e estimaria clientes impactados com base na estratégia descrita.",
        cypher_query: "MATCH (p:Portfolio) RETURN p LIMIT 5",
        impacted_count: 3,
        impacted_preview: [
            {
                account: "005990706",
                name: "Maria Silva",
                advisor_name: "Hugo Dowsley",
                volumeOportunidade: 125_000,
            },
            {
                account: "008001640",
                name: "Lucas Pereira",
                advisor_name: "Ana Souza",
                volumeOportunidade: 88_500,
            },
            {
                account: "014781264",
                name: "Henrique Costa",
                advisor_name: "Paulo Oliveira",
                volumeOportunidade: 210_000,
            },
        ],
        can_commit: true,
        warnings: [],
    };
}

export function buildMockGenericChatAnswer(question?: string): QuestionResponse {
    const q = question?.trim() || "sua pergunta";
    return {
        natural_language_response: [
            "**Assistente (chat genérico — modo design)**",
            "",
            `Você perguntou: *${q}*`,
            "",
            "Em produção, o assistente consultaria o grafo e responderia com insights sobre carteiras, liquidez e oportunidades.",
            "",
            "**Resumo simulado**",
            "- 128 clientes na base",
            "- 42 com perfil moderado",
            "- 18 eventos de liquidez na próxima semana",
        ].join("\n"),
        cypher_query: "MATCH (p:Portfolio) RETURN count(p) AS total_portfolios",
        response_json: "{}",
    };
}

export function buildMockContextualChatAnswer(
    question?: string,
    context?: unknown
): QuestionResponse {
    const q = question?.trim() || "sua pergunta";
    const hasOverview =
        typeof context === "object" &&
        context !== null &&
        "top_clients" in (context as Record<string, unknown>);

    const table = hasOverview
        ? [
              "| Cliente | Perfil | Patrimônio |",
              "| --- | --- | --- |",
              ...mockClientsOverview.top_clients.map(
                  (c) =>
                      `| ${c.cliente.name} | ${c.cliente.perfil} | R$ ${(c.net_worth / 1_000_000).toFixed(2)} mi |`
              ),
          ].join("\n")
        : "| Métrica | Valor |\n| --- | --- |\n| Exemplo | Modo design |";

    return {
        natural_language_response: [
            "**Assistente (chat contextualizado — modo design)**",
            "",
            `Contexto anexado: *${hasOverview ? "visão de clientes" : "dados do painel"}*`,
            "",
            `Pergunta: *${q}*`,
            "",
            "Com base no contexto, estes são os destaques simulados:",
            "",
            table,
            "",
            "_Em produção, a resposta usaria o JSON do contexto enviado pelo painel._",
        ].join("\n"),
        cypher_query:
            "MATCH (p:Portfolio) WHERE toLower(p.perfil) = 'moderado' RETURN p.name, p.account LIMIT 10",
        response_json: JSON.stringify(context ?? {}),
    };
}

export function buildMockMessageSuggestion(context?: Record<string, unknown>): QuestionResponse {
    const clientName =
        (typeof context?.client_name === "string" && context.client_name) ||
        (typeof context?.account === "string" && `cliente ${context.account}`) ||
        "Maria Silva";
    const rawProducts = context?.selected_products
    const products = Array.isArray(rawProducts)
        ? (rawProducts as Array<{ product?: string }>)
        : []
    const productLines =
        products.length > 0
            ? products.map((p, i) => `${i + 1}. ${p.product ?? "Produto exemplo"}`).join("\n")
            : "1. CDB Banco Exemplo 2027\n2. Fundo RF Ativo Design";

    return {
        natural_language_response: [
            `Olá, ${clientName}! Tudo bem?`,
            "",
            "**Contexto do evento**",
            "Identificamos espaço para realocação com foco em renda fixa e liquidez equilibrada.",
            "",
            "**Por que agora**",
            "Há caixa disponível e o perfil do cliente permite alongar prazo com proteção.",
            "",
            "**Sugestão de alocação**",
            productLines,
            "",
            "**Próximo passo**",
            "Posso enviar a proposta formal ou agendar uma conversa rápida para alinhar valores.",
            "",
            "_Mensagem simulada — modo design._",
        ].join("\n"),
        cypher_query: "",
        response_json: JSON.stringify(context ?? {}),
    };
}

export function buildMockSortingChatAnswer(
    rankingText?: string,
    options?: { step?: number; action?: string }
): Record<string, unknown> {
    const step = options?.step ?? 0
    const action = options?.action

    if (step === 1) {
        const confirmed = action !== "cancel"
        return {
            natural_language_response: confirmed
                ? "Ordenação confirmada. As alterações foram aplicadas ao ranking."
                : "Ordenação cancelada. Você pode solicitar uma nova ordenação quando quiser.",
            next_step: confirmed ? 3 : 0,
            ranking_text: rankingText?.trim() || "maior retorno nos últimos 12 meses",
            cypher_query: "MATCH (pr:Product) RETURN pr.nameProduct LIMIT 15",
            response_json: "{}",
        }
    }

    return {
        natural_language_response: [
            "**Ordenação de produtos (modo design)**",
            "",
            `Critério recebido: *${rankingText?.trim() || "maior retorno nos últimos 12 meses"}*`,
            "",
            "Em produção, a IA validaria o critério, geraria a query e pediria confirmação antes de gravar.",
            "",
            "Use os botões abaixo para confirmar ou cancelar esta sugestão.",
        ].join("\n"),
        next_step: 1,
        ranking_text: rankingText?.trim() || "maior retorno nos últimos 12 meses",
        cypher_query: "MATCH (pr:Product) RETURN pr.nameProduct LIMIT 15",
        response_json: "{}",
    }
}

export const mockChatHistory: HistoryMessage[] = [
    {
        question: "Quantos clientes com perfil moderado temos?",
        answer: buildMockGenericChatAnswer(
            "Quantos clientes com perfil moderado temos?"
        ).natural_language_response,
        context: "",
    },
    {
        question: "Quais os maiores patrimônios neste recorte?",
        answer: buildMockContextualChatAnswer(
            "Quais os maiores patrimônios neste recorte?",
            mockClientsOverview
        ).natural_language_response,
        context: mockClientsOverview,
        attachments: [
            {
                filename: "relatorio_carteira.pdf",
                mime_type: "application/pdf",
                s3_key: "llm-sessions/attachments/design-mode/chat_design-mode/relatorio_carteira_mock.pdf",
            },
        ],
    },
];

export { mockCommitments };

const EXECUTION_ADVISORS = ["Ana Ribeiro", "Bruno Tavares", "Carla Menezes", "Diego Nunes", "Elisa Prado"];
const EXECUTION_OPPORTUNITIES = [
    "Aumentar pós-fixado",
    "Resgate NTN-B curto prazo",
    "Portabilidade previdência",
    "Alocação inflação",
];

const executionDays = (start: Date, end: Date): string[] => {
    const dayMs = 24 * 60 * 60 * 1000;
    const total = Math.min(Math.max(1, Math.round((end.getTime() - start.getTime()) / dayMs) + 1), 800);
    return Array.from({ length: total }, (_, i) => new Date(start.getTime() + i * dayMs).toISOString().slice(0, 10));
};

/** Curva acumulada determinística: sobe e satura, como o atingimento real de uma meta. */
const executionCurve = (days: string[], ceiling: number): ExecutionPerformancePoint[] =>
    days.map((date, i) => ({
        date,
        value: Number((ceiling * (1 - Math.exp(-3 * ((i + 1) / days.length)))).toFixed(2)),
    }));

const executionEntity = (id: string, label: string, seed: number): ExecutionEntityPerformance => {
    const totalVolume = 4_000_000 + seed * 1_750_000;
    const totalClients = 40 + seed * 17;
    const ratio = 0.08 + ((seed * 7) % 5) * 0.06;
    return {
        id,
        label,
        totalVolume,
        totalClients,
        executedVolume: Math.round(totalVolume * ratio),
        executedClients: Math.round(totalClients * ratio),
    };
};

const sumExecutionTotals = (
    entities: ExecutionEntityPerformance[],
    pendingFactor: number,
    includePending: boolean
): IExecutionPerformanceResponse["current"] => {
    const settledVolume = entities.reduce((acc, e) => acc + e.executedVolume, 0);
    const settledClients = entities.reduce((acc, e) => acc + e.executedClients, 0);
    // Em trânsito costuma ser maior que o liquidado em janela curta — é o ponto do card.
    const pendingVolume = Math.round(settledVolume * pendingFactor);
    const pendingClients = Math.round(settledClients * pendingFactor);
    return {
        // Mesma regra do backend: com include_pending o em trânsito entra no executado, mas
        // continua reportado à parte. Sem isso o filtro pareceria morto em design mode.
        executedVolume: includePending ? settledVolume + pendingVolume : settledVolume,
        executedClients: includePending ? settledClients + pendingClients : settledClients,
        totalVolume: entities.reduce((acc, e) => acc + e.totalVolume, 0),
        totalClients: entities.reduce((acc, e) => acc + e.totalClients, 0),
        pendingVolume,
        pendingClients,
    };
};

export function buildMockExecutionPerformance(params: {
    start: string;
    end: string;
    includePending?: boolean;
}): IExecutionPerformanceResponse {
    const includePending = params.includePending === true;
    const days = executionDays(new Date(params.start), new Date(params.end));

    const byAdvisor: ExecutionAdvisorPerformance[] = EXECUTION_ADVISORS.map((label, i) => {
        const entity = executionEntity(`advisor-${i}`, label, i + 1);
        const ceiling = (entity.executedVolume / entity.totalVolume) * 100;
        return {
            ...entity,
            pointsByVolume: executionCurve(days, ceiling),
            pointsByClients: executionCurve(days, (entity.executedClients / entity.totalClients) * 100),
            // Vazios como no backend: não há dimensão de filial no grafo.
            branchId: "",
            branchLabel: "",
        };
    });

    const byOpportunity = EXECUTION_OPPORTUNITIES.map((label, i) =>
        executionEntity(`opportunity-${i}`, label, i + 2)
    );

    const current = sumExecutionTotals(byAdvisor, 2.4, includePending);
    const previous = sumExecutionTotals(byOpportunity, 1.8, includePending);

    return {
        start: params.start,
        end: params.end,
        current,
        previous,
        seriesByVolume: executionCurve(days, (current.executedVolume / current.totalVolume) * 100),
        seriesByClients: executionCurve(days, (current.executedClients / current.totalClients) * 100),
        byAdvisor,
        byOpportunity,
        warnings: [],
    };
}
